<template>
  <template v-if="getRoomLogsError">
    <Alert type="error"
           no-icon
           :dismissible="false"
    >
      {{ $t('project.Logs.LogsDataTable.getRoomLogsError') }}
    </Alert>
  </template>
  <template v-else>
    <AppDataTableSkeletonLoader v-if="roomLogsPending"/>

    <v-data-table-server v-else
                         :items="logs"
                         :items-length="logsTotalCount"
                         :must-sort="true"
                         :sort-by="logsSort"
                         :headers="headers"
                         @update:sort-by="onSortChanged"
    >
      <template #item.date="{item}">
        <DateDisplay
          :value="item.date"
          class="text-nowrap"
          with-hours
        />
      </template>
      <template #item.userFullname="{item}">
        <AppTooltip>
          <template #activator="{ props }">
            <div class="text-truncated"
                 style="max-width: 250px"
                 v-bind="props"
            >
              {{ item.userFullname }}
            </div>
          </template>
          <span>{{ item.userFullname }}</span>
        </AppTooltip>
      </template>
      <template #item.name="{item}">
        <AppTooltip>
          <template #activator="{ props }">
            <div class="d-flex align-center"
                 v-bind="props"
            >
              <ClosdFildersIcon :document="item" />
              <span class="text-truncated">{{ getFileName(item.name) }}</span>
            </div>
          </template>
          <span>{{ item.name }}</span>
        </AppTooltip>
      </template>
      <template #item.id="{item}">
        {{ item.fileId || item.directoryId || item.bibleId }}
      </template>
      <template #item.description="{item}">
        <AppTooltip>
          <template #activator="{ props }">
            <div class="text-truncated"
                 style="max-width: 400px"
                 v-bind="props"
            >
              {{ item.description }}
            </div>
          </template>
          <span>{{ item.description }}</span>
        </AppTooltip>
      </template>

      <template #bottom>
        <div class="d-flex justify-end ma-4">
          <AppPagination v-model:current-page="currentPage"
                         v-model:page-size="itemsPerPage"
                         :total="logsTotalCount"
                         @update:current-page="onPageChanged"
                         @update:page-size="onPageSizeChanged"
          />
        </div>
      </template>
    </v-data-table-server>
  </template>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState, useStore } from 'vuex'

import Alert from '@/common/alerts/Alert.vue'
import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import { GET_ROOM_LOGS } from '@/store/modules/logs/action_types'
import { SET_LOGS_SORT, RESET_LOGS_SORT } from '@/store/modules/logs/mutation_types'

const DEFAULT_LOGS_PAGE_SIZE = 50

export default {
  name: 'LogsDataTable',
  components: {
    AppTooltip,
    DateDisplay,
    AppDataTableSkeletonLoader,
    Alert,
    AppPagination,
    ClosdFildersIcon,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  setup: () => {
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: 'logs-items-per-page',
      DEFAULT_LOGS_PAGE_SIZE,
    })

    return {
      currentPage,
      itemsPerPage,
    }
  },
  data () {
    const headersClass = 'text-primary body-2 font-weight-medium white-space-nowrap'
    return {
      getRoomLogsError: false,
      headers: [
        {
          title: this.$t('project.Logs.LogsDataTable.headers.date'),
          value: 'date',
          class: headersClass,
          sortable: true,
        },
        {
          title: this.$t('project.Logs.LogsDataTable.headers.user'),
          value: 'userFullname',
          class: headersClass,
          sortable: true,
        },
        {
          title: this.$t('project.Logs.LogsDataTable.headers.action'),
          value: 'type',
          class: headersClass,
          sortable: true,
        },
        {
          title: this.$t('project.Logs.LogsDataTable.headers.id'),
          value: 'id',
          class: headersClass,
          sortable: true,
        },
        {
          title: this.$t('project.Logs.LogsDataTable.headers.name'),
          value: 'name',
          class: headersClass,
          sortable: true,
        },
        {
          title: this.$t('project.Logs.LogsDataTable.headers.description'),
          value: 'description',
          class: headersClass,
        },
      ],
    }
  },
  computed: {
    ...mapState('logs', ['roomLogs', 'roomLogsPending', 'logsSort', 'logsFilters', 'roomLogsSearchQuery']),
    ...mapGetters('logs', ['getLogsParams']),
    ...mapGetters('user', ['currentUserId']),
    logs () {
      return this.roomLogs?.data ?? []
    },
    logsTotalCount () {
      return this.roomLogs?.pagination.itemCount ?? 0
    },
  },
  watch: {
    logsSort: {
      handler () {
        this.currentPage = 1
        this.prepareGetRoomLogsAtPage(1)
      },
      deep: true,
    },
    logsFilters: {
      handler () {
        this.currentPage = 1
        this.prepareGetRoomLogsAtPage(1)
      },
      deep: true,
    },
    roomLogsSearchQuery () {
      this.currentPage = 1
      this.prepareGetRoomLogsAtPage(1)
    },
  },
  mounted () {
    this.prepareGetRoomLogsAtPage(1)
  },
  beforeUnmount () {
    this.RESET_LOGS_SORT()
  },
  methods: {
    ...mapActions('logs', [GET_ROOM_LOGS]),
    ...mapMutations('logs', [RESET_LOGS_SORT, SET_LOGS_SORT]),
    async prepareGetRoomLogsAtPage (page) {
      try {
        await this.GET_ROOM_LOGS(
          {
            mnemo: this.mnemo,
            params: {
              page,
              pageSize: this.itemsPerPage,
              ...this.getLogsParams,
            },
          })
      } catch {
        this.getRoomLogsError = true
      }
    },
    onPageSizeChanged () {
      this.currentPage = 1
      this.prepareGetRoomLogsAtPage(1)
    },
    onPageChanged (page) {
      this.prepareGetRoomLogsAtPage(page)
    },
    onSortChanged (sort) {
      this.SET_LOGS_SORT(sort)
    },
    getFileName (fileName) {
      if (fileName.length > 40) {
        const splittedFileName = fileName.split('/')
        return `.../${splittedFileName[splittedFileName.length - 1]}`
      }
      return fileName
    },
  },
}
</script>